/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Present safe managed-account state and one display-once reset credential.
 * @state-owner TanStack Query for safe state; component memory for the one-time secret
 * @allowed-imports same module API; app environment; shared modal/loading; qrcode
 * @forbidden secret persistence; credential inference; direct Supabase access
 * @public-api connected classroom roster
 * @side-effects authorized credential read/reset and clipboard copy
 * @tests OrganizationCredentialDialog.test.tsx
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VispoApiError } from '@vispo/api-client';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  type OrganizationCredentialReset,
  organizationCredential,
  resetOrganizationCredential,
} from '../api/organization-credentials';

export function OrganizationCredentialDialog({
  classroomId,
  memberUserId,
  studentName,
}: {
  classroomId: string;
  memberUserId: string;
  studentName: string;
}) {
  const t = useTranslator();
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const [displayOnce, setDisplayOnce] = useState<OrganizationCredentialReset | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const queryKey = ['classroom-organization-credential', classroomId, memberUserId] as const;
  const credential = useQuery({
    queryKey,
    queryFn: () => organizationCredential(environment.apiClient, classroomId, memberUserId),
    retry: false,
  });
  const reset = useMutation({
    mutationFn: () => resetOrganizationCredential(environment.apiClient, classroomId, memberUserId),
    onSuccess: (result) => {
      setDisplayOnce(result);
      queryClient.setQueryData(queryKey, { loginCode: result.loginCode, state: result.state });
    },
  });

  useEffect(() => {
    let active = true;
    if (!displayOnce) {
      setQrDataUrl('');
      return () => {
        active = false;
      };
    }
    void QRCode.toDataURL(displayOnce.activationUrl, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 220,
      color: { dark: '#17131f', light: '#ffffff' },
    }).then((url) => {
      if (active) setQrDataUrl(url);
    });
    return () => {
      active = false;
    };
  }, [displayOnce]);

  const error = credential.error ?? reset.error;

  return (
    <ModalDocument
      className="org-credential-dialog"
      maxWidth={520}
      subtitle={t(`管理 ${studentName} 的學生專用機構帳號`)}
      title={t('機構帳號')}
      footer={
        <>
          <div className="sp" />
          <button
            className="bt pri"
            disabled={credential.isPending || reset.isPending}
            type="button"
            onClick={() => reset.mutate()}
          >
            {reset.isPending ? t('正在產生…') : t('重發一次性代碼')}
          </button>
        </>
      }
    >
      {credential.isPending ? <VispoLoading label={t('正在載入機構帳號')} /> : null}
      {credential.data ? (
        <>
          <SectionLabel first>{t('每日登入帳號')}</SectionLabel>
          <div className="credential-code">{credential.data.loginCode}</div>
          <p className="vmut">{credentialStateLabel(credential.data.state, t)}</p>
        </>
      ) : null}
      {error ? (
        <div className="eqbox" role="alert">
          {credentialErrorMessage(error, t)}
        </div>
      ) : null}
      {displayOnce ? (
        <section className="credential-reveal" aria-live="polite">
          <SectionLabel>{t('只顯示這一次')}</SectionLabel>
          <div className="credential-secret">{displayOnce.oneTimeCode}</div>
          {qrDataUrl ? (
            <img
              alt={t(`${studentName}的機構帳號啟用 QR Code`)}
              className="credential-qr"
              src={qrDataUrl}
            />
          ) : (
            <VispoLoading className="vispo-loading--compact" label={t('正在產生 QR Code')} />
          )}
          <div className="credential-actions">
            <button
              className="bt"
              type="button"
              onClick={() => void navigator.clipboard?.writeText(displayOnce.activationUrl)}
            >
              {t('複製啟用連結')}
            </button>
            <button
              className="bt"
              type="button"
              onClick={() => void navigator.clipboard?.writeText(displayOnce.oneTimeCode)}
            >
              {t('複製一次性代碼')}
            </button>
          </div>
          <p className="vmut">{t('關閉後不會再顯示這組一次性代碼。')}</p>
        </section>
      ) : null}
    </ModalDocument>
  );
}

function credentialStateLabel(state: string, t: (text: string) => string): string {
  if (state === 'active') return t('帳號已啟用，可使用目前密碼登入。');
  if (state === 'disabled') return t('帳號目前已停用。');
  if (state === 'reset_required') return t('學生需要使用新的一次性代碼重設密碼。');
  return t('學生尚未啟用帳號。');
}

function credentialErrorMessage(error: unknown, t: (text: string) => string): string {
  if (error instanceof VispoApiError) {
    if (error.status === 403) return t('你沒有權限管理這位學生的機構帳號。');
    if (error.status === 429) return t('重設次數太密集，請稍後再試。');
  }
  return t('機構帳號服務暫時無法使用，請稍後再試。');
}
